import { TabletButton } from '@campfire/tablet-button';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { GetPublicForm_vm_form as PublicForm } from './__generated__/GetPublicForm';
import { FormikErrorFocus } from '../../../common/form/FormikErrorFocus';
import { TaskFormFieldList } from '../../../common/form/task-form/TaskFormFieldList';
import { TaskFieldContainerProvider } from '../../../common/form/task-form/TaskFieldContainerContext';
import { FormFieldContainer } from '../../../common/inputs/containers/FormFieldContainer';
import { initTaskFormFormikValues, TaskFormValuesType } from '../../../common/form/task-form/model/task-form-init';
import { dynamicValidationSchema } from '../../../common/form/task-form/model/validation-schema';
import { deserializeTaskItems } from '../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { useEndpointFetch } from '../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../global/config/useSnackbar';
import { useSession } from '../../../global/auth/useSession';

const useStyles = makeStyles({
  formFieldsWrapper: {
    '& .form-field-input': {
      '& input, & textarea': {
        padding: '12px 0',
      },
    },
    '& .form-field-outline': {
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: 0,
      borderBottom: '2px solid #e0e0e0',
      transition: 'border-bottom-color 0.2s',
    },
    '& .MuiOutlinedInput-root:hover .form-field-outline': {
      borderBottom: '2px solid #bdbdbd',
    },
    '& .MuiOutlinedInput-root.Mui-focused .form-field-outline': {
      borderBottom: '2px solid #6366f1',
    },
  },
});

interface PublicFormViewProps {
  form: PublicForm;
  onSubmitSuccess: () => void;
}

export const PublicFormView: React.FC<PublicFormViewProps> = ({ form, onSubmitSuccess }) => {
  const classes = useStyles();
  const { user } = useSession();
  const submitFormFetch = useEndpointFetch('/vm/form/submit', { withAuth: false });
  const [submitting, setSubmitting] = useState(false);
  const { setSnackbar } = useSnackbar();
  const [recaptchaRef] = useState<React.RefObject<any>>(React.createRef());

  const taskItems = useMemo(() => {
    const sortedFormItems = [...form.formItems].sort((a, b) => (a.order > b.order ? 1 : -1));

    // Transform nested trigger item references to use taskItemId
    const transformTriggerItems = (triggerItems: any[]) => {
      if (!triggerItems) return triggerItems;
      return triggerItems.map((triggerItem: any) => ({
        ...triggerItem,
        item: triggerItem.item
          ? {
              ...triggerItem.item,
              taskItemId: triggerItem.item.formItemId,
            }
          : triggerItem.item,
      }));
    };

    // Transform options with triggers
    const transformOptions = (options: any[]) => {
      if (!options) return options;
      return options.map((option) => ({
        ...option,
        trigger: option.trigger
          ? {
              ...option.trigger,
              items: transformTriggerItems(option.trigger.items),
            }
          : option.trigger,
      }));
    };

    // Transform field from Form type to Task type
    const transformField = (field: any): any => {
      if (!field) return field;

      if (field.__typename === 'VOLUNTEER_FormDropdownTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_DropdownTaskFieldType',
          dropdownOptions: transformOptions(field.dropdownOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormCheckboxTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_CheckboxTaskFieldType',
          checkboxOptions: transformOptions(field.checkboxOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormMultipleChoiceTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_MultipleChoiceTaskFieldType',
          multipleChoiceOptions: transformOptions(field.multipleChoiceOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormQuizTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_QuizTaskFieldType',
          quizTaskFieldOptions: transformOptions(field.quizTaskFieldOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormMatrixTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_MatrixTaskFieldType',
        };
      }
      return field;
    };

    // Transform form items from Form types to Task types
    // triggerItem is preserved via spread operator for byTriggerOption filtering
    return sortedFormItems.map((formItem) => {
      if (formItem.__typename === 'VOLUNTEER_FormItemFieldType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemFieldType' as const,
          taskItemId: formItem.formItemId,
          field: transformField(formItem.field),
          optional: formItem.optional,
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemHeadingType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemHeadingType' as const,
          taskItemId: formItem.formItemId,
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemContentType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemContentType' as const,
          taskItemId: formItem.formItemId,
          attachments: [],
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemMediaType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemMediaType' as const,
          taskItemId: formItem.formItemId,
        };
      }
      return formItem as any;
    });
  }, [form.formItems]);

  const initialFormikValues = useMemo(() => {
    if (taskItems.length === 0) return undefined;
    return initTaskFormFormikValues(taskItems as any, undefined);
  }, [taskItems]);

  const validationSchema = dynamicValidationSchema(deserializeTaskItems(taskItems as any));

  const handleSubmit = async (values: TaskFormValuesType) => {
    setSubmitting(true);

    const token = await recaptchaRef.current.executeAsync();
    if (!token) {
      setSubmitting(false);
      return;
    }
    recaptchaRef.current.reset();

    const fieldValues = Object.entries(values)
      .map(([taskFieldId, value]) => {
        // Look in transformed taskItems (includes triggered items)
        const taskItem = taskItems.find(
          (item: any) => item.__typename === 'VOLUNTEER_TaskItemFieldType' && item.field?.taskFieldId === taskFieldId
        );
        if (!taskItem || taskItem.__typename !== 'VOLUNTEER_TaskItemFieldType') {
          return null; // Skip unknown fields
        }

        const fieldType = taskItem.field.__typename;

        // Skip empty values - field is either optional or hidden
        // Validation already ensured required visible fields have values
        const isEmpty = (v: any) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

        if (fieldType === 'VOLUNTEER_ShortTextTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_ShortTextTaskFieldValue', taskFieldId, shortTextValue: value };
        }
        if (fieldType === 'VOLUNTEER_LongTextTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_LongTextTaskFieldValue', taskFieldId, longTextValue: value };
        }
        if (fieldType === 'VOLUNTEER_NumberTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_NumberTaskFieldValue', taskFieldId, numericValue: value };
        }
        if (fieldType === 'VOLUNTEER_EmailTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_EmailTaskFieldValue', taskFieldId, emailValue: value };
        }
        if (fieldType === 'VOLUNTEER_PhoneTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_PhoneTaskFieldValue', taskFieldId, phoneValue: value };
        }
        if (fieldType === 'VOLUNTEER_RatingTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_RatingTaskFieldValue', taskFieldId, ratingValue: value };
        }
        if (fieldType === 'VOLUNTEER_AddressTaskFieldType') {
          const addressValue = value as any;
          if (!addressValue?.address?.placesAddress && !addressValue?.address?.manualAddress?.lineOne) return null;
          const address = addressValue?.addressOverride
            ? { manualAddress: addressValue.address?.manualAddress }
            : { placesAddress: addressValue?.address?.placesAddress };
          return {
            __typename: 'VOLUNTEER_AddressTaskFieldValue',
            taskFieldId,
            addressOverride: addressValue?.addressOverride,
            address,
          };
        }
        if (fieldType === 'VOLUNTEER_DateTaskFieldType') {
          // Skip if null - field is either optional or hidden (trigger not selected)
          // Validation already ensured required visible fields have values
          if (!value) return null;
          return { __typename: 'VOLUNTEER_DateTaskFieldValue', taskFieldId, dateValue: value };
        }
        if (fieldType === 'VOLUNTEER_SignatureTaskFieldType') {
          if (!value) return null;
          return { __typename: 'VOLUNTEER_SignatureTaskFieldValue', taskFieldId, signature: value };
        }
        if (fieldType === 'VOLUNTEER_AttachmentTaskFieldType') {
          if (!value) return null;
          return { __typename: 'VOLUNTEER_AttachmentTaskFieldValue', taskFieldId, attachmentValue: value };
        }
        // Dynamic field types (transformed to Task types)
        if (fieldType === 'VOLUNTEER_DropdownTaskFieldType') {
          if (isEmpty(value)) return null;
          return {
            __typename: 'VOLUNTEER_DropdownTaskFieldValue',
            taskFieldId,
            dropdownTaskFieldOptionId: value,
          };
        }
        if (fieldType === 'VOLUNTEER_CheckboxTaskFieldType') {
          if (isEmpty(value)) return null;
          return {
            __typename: 'VOLUNTEER_CheckboxTaskFieldValue',
            taskFieldId,
            checkboxTaskFieldOptionIds: value,
          };
        }
        if (fieldType === 'VOLUNTEER_MultipleChoiceTaskFieldType') {
          const mcValue = value as any;
          if (!mcValue?.multipleChoiceOptionId) return null;
          return {
            __typename: 'VOLUNTEER_MultipleChoiceTaskFieldValue',
            taskFieldId,
            multipleChoiceTaskFieldOptionId: mcValue.multipleChoiceOptionId,
            multipleChoiceText: mcValue.multipleChoiceText,
          };
        }
        if (fieldType === 'VOLUNTEER_QuizTaskFieldType') {
          const quizValue = value as any;
          if (!quizValue?.quizTaskFieldOptionId) return null;
          return {
            __typename: 'VOLUNTEER_QuizTaskFieldValue',
            taskFieldId,
            quizTaskFieldOptionId: quizValue.quizTaskFieldOptionId,
            quizText: quizValue.quizText,
          };
        }
        if (fieldType === 'VOLUNTEER_TimeTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_TimeTaskFieldValue', taskFieldId, timeValue: value };
        }
        if (fieldType === 'VOLUNTEER_UrlTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_UrlTaskFieldValue', taskFieldId, urlValue: value };
        }
        if (fieldType === 'VOLUNTEER_YesNoTaskFieldType') {
          if (value === null || value === undefined) return null;
          return { __typename: 'VOLUNTEER_YesNoTaskFieldValue', taskFieldId, yesNoValue: value };
        }
        if (fieldType === 'VOLUNTEER_LinearScaleTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_LinearScaleTaskFieldValue', taskFieldId, linearScaleValue: value };
        }
        if (fieldType === 'VOLUNTEER_NpsTaskFieldType') {
          if (isEmpty(value)) return null;
          return { __typename: 'VOLUNTEER_NpsTaskFieldValue', taskFieldId, npsValue: value };
        }
        if (fieldType === 'VOLUNTEER_MatrixTaskFieldType') {
          if (isEmpty(value)) return null;
          // value is an object mapping rowId -> columnId
          const matrixEntries = Object.entries(value as Record<string, string>)
            .filter(([, columnId]) => columnId)
            .map(([rowId, columnId]) => ({ rowId, columnId }));
          if (matrixEntries.length === 0) return null;
          return { __typename: 'VOLUNTEER_MatrixTaskFieldValue', taskFieldId, matrixEntries };
        }

        // Unknown field type - skip if empty, otherwise pass through
        if (isEmpty(value)) return null;
        return { taskFieldId, value };
      })
      .filter(Boolean); // Remove null entries (optional/hidden fields with no value)

    const ipAddress = '';
    const { userAgent } = navigator;
    const { referrer } = document;

    try {
      const response = await submitFormFetch.run({
        formId: form.formId,
        fieldValues,
        ipAddress,
        userAgent,
        referrer,
        profileId: user?.profileId ?? null,
      });

      if (response.ok) {
        onSubmitSuccess();
      } else {
        const errorMessage = response.data?.message || 'Failed to submit form. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          variant: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while submitting the form. Please try again.',
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!initialFormikValues) {
    return null;
  }

  return (
    <Box>
      {form.description && (
        <Box marginBottom={2} paddingLeft={1} paddingRight={1}>
          <Typography variant='body2' color='textSecondary' style={{ fontSize: '15px' }}>
            {form.description}
          </Typography>
        </Box>
      )}

      <TaskFieldContainerProvider container={FormFieldContainer}>
        <Box className={classes.formFieldsWrapper}>
          <Formik initialValues={initialFormikValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
            {({ values, isValid, dirty }) => {
              const isFormComplete = isValid && dirty;
              return (
                <Form>
                  <ReCAPTCHA ref={recaptchaRef} size='invisible' sitekey='6Lez7NsUAAAAAOTVaiFrSnXTvl4XPX7ivvVXzruA' />
                  <TaskFormFieldList taskItems={deserializeTaskItems(taskItems as any)} values={values} />
                  <Box width={1} display='flex' justifyContent='flex-end' paddingTop={3} paddingRight={1}>
                    <TabletButton
                      color='primary'
                      variant='contained'
                      type='submit'
                      size='large'
                      style={{
                        borderRadius: 4,
                        minWidth: 120,
                        opacity: !isFormComplete || submitting ? 0.5 : 1,
                        cursor: !isFormComplete || submitting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </TabletButton>
                  </Box>
                  <FormikErrorFocus />
                </Form>
              );
            }}
          </Formik>
        </Box>
      </TaskFieldContainerProvider>
    </Box>
  );
};
